import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Box, Theme, Typography, useMediaQuery } from '@mui/material'

import { Modal } from '../../../components/Modal'
import { TestIds } from '../../../testIds'
import { SeedSearch } from './SeedSearch'
import { useSelector } from 'react-redux'
import { selectContactInfo, selectCurrentFarmerAccount } from '../../azure-b2c/storage'
import { Product } from '../../products/storage'
import { DateRangeType } from '../../../components/DateRangePicker'
import ProductFilter, { ComponentFilters } from '../../products/components/ProductFilter'
import { getCategories } from '../../typesense/api'
import { SEED_MIX_TYPE } from '../types/SeedOrderFormType'
import { CUSTOM_SEED_FILTERED_CATEGORIES } from '../../typesense/utils'
import { selectSeedOrder } from '../storage/seedCart.slice'
import { useSeedBranch } from '../../../util/useBranch'
import { selectStores } from '../storage/seedStores.slice'
import { locationToSiteId } from '../../stores/storage/utils'

const TypesenseFilters = [ComponentFilters.SEARCH_TYPESENSE, ComponentFilters.CATEGORY]

const AddSeedModal = ({
  seedType,
  setSeedType,
  seedId,
}: {
  seedType?: string
  setSeedType: React.Dispatch<React.SetStateAction<string | undefined>>
  seedId?: string
}) => {
  const [categories, setCategories] = useState<string[]>([])
  const account = useSelector(selectCurrentFarmerAccount)
  const seedOrders = useSelector(selectSeedOrder(account?.accountNumber))
  const seedMix = seedOrders?.mixes.find((mix) => mix.id === seedId)
  const defaultBranch = useSeedBranch()
  const stores = useSelector(selectStores)

  useEffect(() => {
    if (seedMix && stores.length) {
      let value = seedMix.storeId ?? defaultBranch
      const storeIdIsLocationId = value?.includes('-')
      if (storeIdIsLocationId && value) {
        value = locationToSiteId(value)
      }
      setStoreId(value)
    }
  }, [seedMix])

  useEffect(() => {
    ;(async () => {
      const { categories: newCategories } = await getCategories()
      const seedCategories = newCategories.filter((value) => {
        return value.value.startsWith('Seed>')
      })
      const seedSubcategories = seedCategories
        .map((value) => value.value)
        .filter((value) => value !== '')

      const filteredCategory =
        seedType !== SEED_MIX_TYPE.PREMIX.toString()
          ? seedSubcategories.filter((x) => {
              if (x.startsWith('Seed')) {
                const subCategories = x.split('>')
                if (subCategories.length > 1) {
                  return CUSTOM_SEED_FILTERED_CATEGORIES.indexOf(subCategories[1]) < 0
                }
                return true
              }
              return true
            })
          : seedSubcategories

      setCategories(filteredCategory)
    })()
  }, [seedType])

  const { open, setOpen, branchId } = useAddSeedModal()

  useEffect(() => {
    if (!open) {
      setCurrentPage(0)
      setSortBy('')
      setCategory('Seed>')
      setSearchText('')
      setSearchInput('')
    }
  }, [open])

  const handleCloseModal = () => {
    setOpen(false)
  }

  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))

  const selectedAccount = useSelector(selectCurrentFarmerAccount)
  const data: Product[] = []
  const relationshipOwner = useSelector(selectContactInfo)

  const handleClearFilters = () => {
    // console.log('test')
  }

  const isFilterApplied = false
  const [currentPage, setCurrentPage] = useState(0)
  const [sortBy, setSortBy] = useState('')

  const [inStock, setInStock] = useState<boolean>(false)
  const [category, setCategory] = useState('Seed>')
  const [searchText, setSearchText] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [storeId, setStoreId] = useState<string | undefined>(undefined)
  const [dateRange, setDateRange] = useState<DateRangeType>({
    from: undefined,
    to: undefined,
  })
  const [priceRange, setPriceRange] = useState<{
    to: undefined | number
    from: undefined | number
  }>({
    from: undefined,
    to: undefined,
  })
  useEffect(() => {
    if (!storeId) {
      setStoreId(branchId)
    }
  }, [branchId])

  const selectedFilters = useMemo(() => {
    return {
      category: { value: category, setValue: setCategory },
      dateRange: { value: dateRange, setValue: setDateRange },
      searchText: { value: searchText, setValue: setSearchText },
      searchInput: { value: searchInput, setValue: setSearchInput },
      priceRange: { value: priceRange, setValue: setPriceRange },
      inStock: { value: inStock, setValue: setInStock },
      seedType: { value: seedType, setValue: setSeedType },
      storeId: { value: branchId, setValue: setStoreId },
    }
  }, [
    category,
    dateRange,
    inStock,
    seedType,
    branchId,
    priceRange,
    searchText,
    searchInput,
    setCategory,
    setDateRange,
    setInStock,
    setSeedType,
    setStoreId,
    setPriceRange,
    setSearchText,
    setSearchInput,
  ])

  return (
    <Modal
      title='Seed product'
      header={
        <>
          <Typography variant='body2'>
            Choose the items you want for your order or filter the selection by category.
          </Typography>
          <ProductFilter
            components={TypesenseFilters}
            filters={{ categories }}
            selectedFilters={selectedFilters}
            items={data}
          />
        </>
      }
      open={open}
      handleClose={handleCloseModal}
      testId={TestIds.feat.seed.seedSearch.separateSeed}
      maxWidth='md'
      paperProps={{
        sx: { height: isSm ? 'inherit' : '844px', overflow: 'hidden' },
      }}
    >
      <Box overflow='hidden'>
        <SeedSearch
          accountNumber={selectedAccount?.accountNumber}
          data={[]}
          handleClearFilters={handleClearFilters}
          isFilterApplied={isFilterApplied}
          sortBy={sortBy}
          setSortBy={setSortBy}
          selectedFilters={selectedFilters}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          relationshipOwner={relationshipOwner}
          seedId={seedId}
        />
      </Box>
    </Modal>
  )
}

const AddSeedModalContext = React.createContext<
  | {
      open: boolean
      setOpen: React.Dispatch<React.SetStateAction<boolean>>
      callback: (item: unknown) => Promise<unknown>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setCallback: React.Dispatch<React.SetStateAction<(item: any) => Promise<void>>>
      seedType: string | undefined
      setSeedType: React.Dispatch<React.SetStateAction<string | undefined>>
      setSeedId: (seedId: string | undefined) => void
      branchId: string | undefined
      setBranchId: React.Dispatch<React.SetStateAction<string | undefined>>
    }
  | undefined
>(undefined)

export const AddSeedModalProvider: React.FC<{
  children: React.ReactNode | React.ReactNode[]
}> = ({ children }) => {
  const [open, setOpen] = useState(false)
  const [seedType, setSeedType] = useState<string | undefined>()
  const [seedId, setSeedId] = useState<string | undefined>()
  const [branchId, setBranchId] = useState<string | undefined>()
  const [callback, setCallback] = useState<(item: unknown) => Promise<void>>(() => async () => {
    //
  })
  const account = useSelector(selectCurrentFarmerAccount)
  const seedOrders = useSelector(selectSeedOrder(account?.accountNumber))
  const defaultBranch = useSeedBranch()
  const stores = useSelector(selectStores)

  const _setSeedId = React.useCallback(
    (seedId: string | undefined) => {
      const seedMix = seedOrders?.mixes.find((mix) => mix.id === seedId)

      if (seedMix && stores.length) {
        let value = seedMix.storeId ?? defaultBranch
        if (seedMix.storeId && stores.map((a) => a.id).includes(seedMix.storeId)) {
          value = defaultBranch
        }
        const storeIdIsLocationId = value?.includes('-')

        if (storeIdIsLocationId && value) {
          value = locationToSiteId(value)
        }
        setBranchId(value)
      }
    },
    [seedOrders, stores],
  )

  const value = React.useMemo(() => {
    return {
      open,
      setOpen,
      callback,
      seedType,
      setCallback,
      setSeedType,
      setSeedId: _setSeedId,
      setBranchId,
      branchId,
    }
  }, [
    open,
    setOpen,
    callback,
    seedType,
    setCallback,
    setSeedType,
    setSeedId,
    _setSeedId,
    setBranchId,
    branchId,
  ])

  return (
    <AddSeedModalContext.Provider value={value}>
      {children}
      <AddSeedModal seedType={seedType} setSeedType={setSeedType} seedId={seedId} />
    </AddSeedModalContext.Provider>
  )
}

export const useAddSeedModal = () => {
  const context = useContext(AddSeedModalContext)
  if (!context) {
    throw new Error('useAddSeedModal must be used within a AddSeedModalContext')
  }
  return context
}
